import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV } from "@/constants/testIds";

const navItems = [
  { to: "/", label: "Home", testid: NAV.home },
  { to: "/about", label: "About", testid: NAV.about },
  { to: "/services", label: "Services", testid: NAV.services },
  { to: "/portfolio", label: "Work", testid: NAV.portfolio },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div
          className={`glass rounded-full flex items-center justify-between px-5 md:px-7 transition-all duration-500 ${
            scrolled ? "py-2.5" : "py-3.5"
          }`}
        >
          <Link
            to="/"
            data-testid={NAV.logo}
            className="flex items-center gap-2.5 group"
          >
            <span className="relative h-7 w-7 rounded-full overflow-hidden bg-[#e03c31] flex items-center justify-center ring-1 ring-white/10">
              <img
                src="https://themestudios.in/assets/branding/ts-logo-light.webp"
                alt="ThemeStudios"
                className="h-full w-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </span>
            <span className="font-cabinet font-black text-base tracking-tight uppercase">
              ThemeStudios
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                end={it.to === "/"}
                className={({ isActive }) =>
                  `px-3.5 py-2 text-[13px] font-medium tracking-wide rounded-full transition-colors ${
                    isActive
                      ? "text-white bg-white/10"
                      : "text-zinc-400 hover:text-white"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/contact"
              data-testid={NAV.contact}
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-bold uppercase tracking-[0.12em] rounded-full bg-white text-black hover:bg-[#e03c31] hover:text-white transition-colors"
            >
              Start a Brief <ArrowUpRight size={14} />
            </Link>
            <button
              data-testid={NAV.menuToggle}
              onClick={() => setOpen(!open)}
              className="md:hidden h-9 w-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"
              aria-label="Toggle menu"
            >
              {open ? <X size={16} /> : <Menu size={16} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden mt-3 glass rounded-3xl p-4 flex flex-col">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={`mobile-${it.testid}`}
                end={it.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-sm font-medium ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-zinc-400 hover:text-white"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-2 px-4 py-3 rounded-xl text-sm font-bold uppercase tracking-wider bg-[#e03c31] text-white text-center"
            >
              Start a Brief
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
